import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { TargetDataType } from './programBank';

/*
 * Saved graph configuration per target. Server:
 * [server/src/routes/target_graph_configs.js].
 *
 * The UI edits config in local state; only "Save as default" fires the PUT
 * and invalidates this query. "Reset to default" fires the DELETE so the
 * next GET returns 204 and the UI falls back to hard-coded defaults.
 */

export type ChartType   = 'line' | 'bar';
export type Aggregation = 'raw' | 'daily_avg' | 'weekly_avg' | 'cumulative';
export type DateRange   = '30d' | '60d' | '90d' | 'all' | 'custom';

export interface GraphConfig {
    chartType:     ChartType;
    aggregation:   Aggregation;
    dateRange:     DateRange;
    customStart:   string | null;
    customEnd:     string | null;
    showCriterion: boolean;
    showTrendline: boolean;
    yAxisMin:      number | null;
    yAxisMax:      number | null;
}

export interface SavedGraphConfig {
    id:                    string;
    clientProgramTargetId: string;
    config:                GraphConfig;
    updatedBy:             string | null;
    updatedAt:             string;
}

// Defaults depend on the target's data type:
// percent-like series pin the y-axis to 0–100; frequency/duration start at 0
// and leave the max auto-scaled to the data.
export function getDefaultGraphConfig(dataType: TargetDataType): GraphConfig {
    const isPercent = dataType === 'percent' || dataType === 'trial' || dataType === 'task_analysis';
    return {
        chartType:     'line',
        aggregation:   'raw',
        dateRange:     'all',
        customStart:   null,
        customEnd:     null,
        showCriterion: true,
        showTrendline: false,
        yAxisMin:      0,
        yAxisMax:      isPercent ? 100 : null,
    };
}

export function useTargetGraphConfig(targetId: string | null) {
    return useQuery({
        queryKey: ['target-graph-config', targetId] as const,
        queryFn: async (): Promise<SavedGraphConfig | null> => {
            if (!targetId) return null;
            const res = await api.get<SavedGraphConfig | ''>(`/target-graph-configs/${targetId}`);
            // 204 → axios returns empty string as `data`; treat as "no saved config".
            if (!res.data || typeof res.data === 'string') return null;
            return res.data;
        },
        enabled: !!targetId,
        staleTime: 60_000,
    });
}

export function useSaveGraphConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ targetId, config }: { targetId: string; config: GraphConfig }) => {
            const res = await api.put<SavedGraphConfig>(`/target-graph-configs/${targetId}`, config);
            return res.data;
        },
        onSuccess: (row) => {
            qc.invalidateQueries({ queryKey: ['target-graph-config', row.clientProgramTargetId] });
        },
    });
}

export function useResetGraphConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (targetId: string) => {
            await api.delete(`/target-graph-configs/${targetId}`);
            return targetId;
        },
        onSuccess: (targetId) => {
            qc.invalidateQueries({ queryKey: ['target-graph-config', targetId] });
        },
    });
}
